import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export function useGpsRealtime(companyId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!companyId) return;

    console.log('🔌 Setting up realtime subscriptions for company:', companyId);

    // Subscribe to GPS position updates
    const positionsChannel = supabase
      .channel(`gps-positions-${companyId}`, {
        config: {
          broadcast: { self: false },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'fleet_gps',
          table: 'positions',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('📍 New GPS position received:', payload);
          
          // Invalidate and refetch GPS devices to update their last position
          queryClient.invalidateQueries({ queryKey: ['gps-devices', companyId] });
          
          // Optionally update specific device cache
          if (payload.new?.device_id) {
            queryClient.invalidateQueries({ 
              queryKey: ['gps-device', payload.new.device_id] 
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'fleet_gps',
          table: 'devices',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('📱 GPS device updated:', payload);
          
          // Update devices cache
          queryClient.invalidateQueries({ queryKey: ['gps-devices', companyId] });
        }
      )
      .subscribe((status) => {
        console.log('📡 Positions/Devices channel status:', status);
      });

    // Subscribe to alert events
    const alertsChannel = supabase
      .channel(`gps-alerts-${companyId}`, {
        config: {
          broadcast: { self: false },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'fleet_gps',
          table: 'alert_events',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          console.log('🚨 New GPS alert received:', payload);
          
          // Show notification for new alerts
          if (payload.new) {
            // You can add toast notification here
            queryClient.invalidateQueries({ queryKey: ['gps-alerts', companyId] });
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Alerts channel status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up realtime subscriptions for company:', companyId);
      supabase.removeChannel(positionsChannel);
      supabase.removeChannel(alertsChannel);
    };
  }, [companyId, queryClient]);
}